import React, { useState, useEffect, useCallback } from 'react'
import './SignUp.less'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client';
import { SIGN_UP_MUTATION, GET_CURRENT_USER_WITH_SPONSOR_ID } from '../../shared/graphql/auth/auth.gql';
import { GET_SERVICE_BY_DROPDOWN } from '../../shared/graphql/user-management.gql';
import { GET_CONFIGURATION } from '../../shared/graphql/configuration.gql';
import { authClient } from '../../apollo-clients';
import { RegexExpression } from '../../shared/helpers/regex.helper';
import { PasswordHelper } from '../../shared/helpers/password.helper';
import PasswordRequirements from '../../shared/components/password-requirements/PasswordRequirements';
import { Input, Select } from 'antd';
import Modal from '../../shared/components/modal/Modal';
import { CountryCodeOptions, PhoneNumberUtils } from "../../shared/helpers/countryCode/countryCode.helper";

const SignUp = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [signUpSuccess, setSignUpSuccess] = useState(false);
  const [signUpUserId, setSignUpUserId] = useState('');
  const [error, setError] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [sponsorName, setSponsorName] = useState('');
  const [sponsorError, setSponsorError] = useState('');
  const navigate = useNavigate();

  // Form field definitions - single source of truth
  const fieldDefinitions = [
    { 
      name: 'sponsorId', 
      type: 'text', 
      label: 'Sponsor ID', 
      required: true,
      placeholder: 'Enter sponsor ID'
    },
    { 
      name: 'name', 
      type: 'text', 
      label: 'Full Name', 
      required: true,
      placeholder: 'Enter your full name',
      validation: (value: string) => {
        if (value.length < 2 || value.length > 50) {
          return 'Name must be between 2-50 characters';
        }
        return null;
      }
    },
    { 
      name: 'email', 
      type: 'email', 
      label: 'Email', 
      required: true,
      validation: (value: string) => {
        if (!RegexExpression.EmailPattern.test(value)) {
          return 'Please enter a valid email address';
        }
        return null;
      }
    },
    { 
      name: 'phone', 
      type: 'phone', 
      label: 'Phone Number', 
      required: true,
      validation: (value: string, formData: any) => {
        if (!PhoneNumberUtils.validate(value, formData.countryCode)) {
          return 'Invalid phone number';
        }
        return null;
      }
    },
    { 
      name: 'gender', 
      type: 'select', 
      label: 'Gender', 
      required: true,
      options: [
        { label: 'Male', value: 'MALE' },
        { label: 'Female', value: 'FEMALE' },
        { label: 'Prefer not to disclose', value: 'PREFER_NOT_TO_DISCLOSE' }
      ]
    },
    { 
      name: 'package', 
      type: 'select', 
      label: 'Category', 
      required: true,
      options: [
        { label: 'RM 5', value: '5'},
        { label: 'RM 10', value: '10'},
        { label: 'RM 15', value: '15'},
        { label: 'RM 20', value: '20'}
      ]
    },
    { 
      name: 'serviceBy', 
      type: 'async-select', 
      label: 'Service By', 
      required: true
    },    
    { 
      name: 'password', 
      type: 'password', 
      label: 'Password', 
      required: true,
      validation: (value: string) => {
        // No format validation - just check if not empty
        return value && value.trim().length > 0 ? null : 'Password cannot be empty';
      }
    },    { 
      name: 'confirmPassword', 
      type: 'password', 
      label: 'Confirm Password', 
      required: true,
      validation: (value: string, formData: any) => {
        return PasswordHelper.validateConfirmPassword(formData.password, value) 
          ? null 
          : 'Passwords do not match';
      }
    }
  ];

  // Initialize form data from field definitions
  const [formData, setFormData] = useState(
    fieldDefinitions.reduce((acc, field) => {
      acc[field.name] = '';
      return acc;
    }, {} as Record<string, any>)
  );

  // Check if sponsor ID is from referral link
  const isFromReferralLink = searchParams.get('sponsor') !== null;

  // Auto-fill sponsor ID from URL parameters
  useEffect(() => {
    const sponsorFromUrl = searchParams.get('sponsor');
    if (sponsorFromUrl) {
      setFormData(prev => ({ ...prev, sponsorId: sponsorFromUrl }));
    }
  }, [searchParams]);
  
  const [signUpMutation] = useMutation(SIGN_UP_MUTATION, {
    client: authClient,
  });

  const [getCurrentUserWithSponsorIdMutation] = useMutation(GET_CURRENT_USER_WITH_SPONSOR_ID, {
    client: authClient,
  });

  // Function to fetch sponsor name by ID
  const handleUnfocusSponsorId = useCallback(async () => {
    if (formData.sponsorId && formData.sponsorId.trim() !== '') {
      setSponsorError('');
      setSponsorName('');
      
      try {
        const result = await getCurrentUserWithSponsorIdMutation({
          variables: { input: { sponsorId: formData.sponsorId.trim() } }
        });
        
        const response = result.data?.getCurrentUserWithSponsorId;
        if (response?.success && response.sponsor) {
          setSponsorName(response.sponsor.name);
          // Update the form data with the confirmed sponsor ID
        } else {
          // Handle unsuccessful response
          setSponsorError(response?.message || 'Sponsor ID not found or invalid');
          setSponsorName('');
        }
      } catch (error: any) {
        console.error('Error fetching sponsor:', error);
        setSponsorError('Sponsor ID not found or invalid');
        setSponsorName('');
      }
    } else {
      setSponsorName('');
      setSponsorError('');
    }
  }, [formData.sponsorId, getCurrentUserWithSponsorIdMutation]);

  // Automatically fetch sponsor name for pre-filled sponsor ID
  useEffect(() => {
    if (isFromReferralLink && formData.sponsorId) {
      const timer = setTimeout(() => {
        handleUnfocusSponsorId();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [formData.sponsorId, isFromReferralLink, handleUnfocusSponsorId]);

  // Fetch service providers for dropdown
  const { data: serviceProvidersData, loading: serviceProvidersLoading } = useQuery(GET_SERVICE_BY_DROPDOWN, {
    client: authClient, // Use non-authenticated client for public access
    errorPolicy: 'all'
  });

  // Fetch service-by-role configuration
  const { data: serviceByRoleConfig } = useQuery(GET_CONFIGURATION, {
    variables: { id: 'service-by-role' },
    client: authClient,
    errorPolicy: 'all'
  });

  // Dynamic validation using field definitions
  const validateForm = () => {
    setError(''); // Clear previous errors
    
    for (const field of fieldDefinitions) {
      const value = formData[field.name];
      
      // Check required fields
      if (field.required && (!value || value.toString().trim() === '')) {
        setError(`${field.label} is required`);
        return false;
      }
      
      // Run field-specific validation
      if (field.validation && value) {
        const validationError = field.validation(value, formData);
        if (validationError) {
          setError(validationError);
          return false;
        }
      }
    }
    
    return true;
  };

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Track password changes for requirements display
    if (field === 'password') {
      setCurrentPassword(value as string);
    }
    
    // Clear sponsor info when sponsor ID changes
    if (field === 'sponsorId') {
      setSponsorName('');
      setSponsorError('');
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    console.log(formData);
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError('');
    
    try {
      const result = await signUpMutation({
        variables: {
          input: {
            sponsorId: formData.sponsorId,
            name: formData.name,
            email: formData.email,
            phone: formData.countryCode + formData.phone,
            gender: formData.gender || undefined,
            password: formData.password,
            package: formData.package,
            serviceBy: formData.serviceBy || undefined,
          }
        }
      });

      if (result.data?.signUp?.success) {
        setSignUpSuccess(true);
        setSignUpUserId(result.data?.signUp?.user?.userName || '');
      } else {
        setError(result.data?.signUp?.message || 'Registration failed. Please try again.');
      }
    } catch (err: any) {
      console.error('Sign up failed:', err);
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    const wasSuccess = signUpSuccess;
    
    setSignUpSuccess(false);
    setSignUpUserId('');
    setError('');
    
    // Navigate only if it was a success modal
    if (wasSuccess) {
      navigate('/login', { replace: true });
    }
  };

  const handleCountryCodeChange = (value: string) => {
    setFormData(prev => ({ ...prev, phone: PhoneNumberUtils.format(prev.phone).value, countryCode: value }));
  };

  // Dynamic field renderer
  const renderField = (field: any) => {
    const commonProps = {
      id: field.name,
      value: formData[field.name],
      disabled: loading,
    };

    switch (field.type) {
      case 'text':
      case 'email':
      case 'password':
        return (
          <input
            {...commonProps}
            type={field.type}
            placeholder={field.placeholder}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            style={field.name === 'sponsorId' && isFromReferralLink ? { borderColor: '#52c41a' } : {}}
            onBlur={field.name === 'sponsorId' ? handleUnfocusSponsorId : undefined}
          />
        );

      case 'phone':
        return (
          <Input 
            size="large" 
            addonBefore={
              <Select 
                showSearch 
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                style={{ width: "120px", minWidth: "120px"}} 
                options={CountryCodeOptions}
                defaultValue={formData.countryCode}
                placeholder="Country"
                popupRender={(menu) => <div style={{ minWidth: "100px" }}>{menu}</div>}
                onChange={handleCountryCodeChange}
              />
            } 
            id={field.name} 
            value={PhoneNumberUtils.format(formData[field.name]).value} 
            onChange={(e) => handleInputChange(field.name, e.target.value)} 
            disabled={loading}
          />
        );

      case 'select':
        return (
          <Select
            {...commonProps}
            size="large"
            placeholder={`Select ${field.label.toLowerCase()}`}
            onChange={(value) => handleInputChange(field.name, value)}
            options={field.options}
            style={{ width: '100%' }}
          />
        );

      case 'async-select':
        const isServiceBy = field.name === 'serviceBy';
        const serviceByData = isServiceBy ? serviceProvidersData?.getServiceByDropdown : [];
        
        // Get service-by roles from configuration and parse comma-separated values
        const serviceByRolesValue = serviceByRoleConfig?.getConfiguration?.value || '';
        const serviceByRoles = serviceByRolesValue 
          ? serviceByRolesValue.split(',').map((role: string) => role.trim()).filter(Boolean)
          : [];
        
        // Convert roles to dropdown format
        const configRoleOptions = serviceByRoles.map((role: string) => ({
          id: role.toLowerCase().replace(/\s+/g, '-'),
          key: role.toLowerCase().replace(/\s+/g, '-'), 
          value: role.toLowerCase().replace(/\s+/g, '-'),
          label: role,
          description: `${role} Role`
        }));
        
        // Combine service providers with configuration-based roles
        const data = isServiceBy ? [
          ...(serviceByData || []),
          ...configRoleOptions
        ] : [];
        
        const isLoading = isServiceBy ? serviceProvidersLoading : false;

        return (
          <Select
            {...commonProps}
            size="large"
            placeholder={`Select ${field.label.toLowerCase()}`}
            onChange={(value) => handleInputChange(field.name, value)}
            disabled={loading || isLoading}
            allowClear
            showSearch
            filterOption={(input, option) => {
              const optionText = data?.find((item: any) => item.id === option?.value)?.label || '';
              return optionText.toLowerCase().includes(input.toLowerCase());
            }}
            style={{ width: '100%' }}
            popupMatchSelectWidth={true}
            getPopupContainer={(trigger) => trigger.parentElement}
          >
            {data?.map((item: any) => (
              <Select.Option key={item.id || item.value} value={item.id}>
                <div>
                  <div>
                    {item.label}
                  </div>
                </div>
              </Select.Option>
            ))}
          </Select>
        );

      default:
        return null;
    }
  };

  return (
    <div className="signup-container">
      <div className="signup-card">
        <div className="signup-header">
          <h2>Get Started</h2>
          {isFromReferralLink ? (
            <div>
              <p style={{ color: '#52c41a', marginBottom: '4px' }}>
                🎉 You've been invited to join!
              </p>
              <p>Create your account to get started</p>
            </div>
          ) : (
            <p>Create your account</p>
          )}
        </div>

        <form className="signup-form" onSubmit={handleSignUp}>
          <div className="form-row">
            {fieldDefinitions.slice(0, 5).map((field) => (
              <div key={field.name} className="form-group">
                <label htmlFor={field.name}>
                  {field.label} {field.required && <span className="required">*</span>}
                  {field.name === 'sponsorId' && isFromReferralLink && (
                    <span style={{ color: '#52c41a', fontSize: '12px', marginLeft: '8px' }}>
                      ✓ Pre-filled from referral link
                    </span>
                  )}
                </label>
                {renderField(field)}
                {field.name === 'sponsorId' && (
                  <div style={{ marginTop: '4px' }}>
                    {sponsorName && (
                      <div style={{ color: '#52c41a', fontSize: '12px' }}>
                        ✓ Sponsor: {sponsorName}
                      </div>
                    )}
                    {sponsorError && (
                      <div style={{ color: '#ff4d4f', fontSize: '12px' }}>
                        ⚠ {sponsorError}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className="form-row">
            {fieldDefinitions.slice(5).map((field) => (
              <div key={field.name} className="form-group">
                <label htmlFor={field.name}>
                  {field.label} {field.required && <span className="required">*</span>}
                </label>
                {renderField(field)}
                {field.name === 'password' && (
                  <PasswordRequirements 
                    password={currentPassword}
                    showProgress={true}
                    showRequirements={true}
                    compact={false}
                  />
                )}
              </div>
            ))}
          </div>

          <button 
            type="submit" 
            className="signup-btn primary"
            disabled={loading}
          >
            {loading ? 'Creating Account...' : 'Sign Up'}
          </button>
        </form>

        <div className="signup-footer">
          <p>Already have an account? <Link to="/login">Sign in</Link></p>
        </div>
        <Modal 
          message={
            signUpSuccess 
              ? `Sign up successful!\n\nYour User ID: ${signUpUserId}\n\nPlease use this User ID to login.`
              : error
          }
          success={signUpSuccess}
          onClose={handleModalClose}
          showCancelButton={false}
        />
      </div>
    </div>
  );
};

export default SignUp;